// CustomSider.helpers — role gating for sider menu items.
//
// Split out of `CustomSider.tsx` so that module only exports components
// (react-refresh/only-export-components).

import type { EmployeeRole } from "../types/enums";
import { ACCESS_GATES, type AccessGate } from "@ds/config/accessGates";

const SIDER_KEY_GATES: Record<string, AccessGate> = {
  "settings":           "settings_show",
  "/settings":          "settings_show",
  "invoices-parent":    "invoice_show",
  "/invoices-parent":   "invoice_show",
  "invoices-issued":    "invoice_show",
  "/invoices-issued":   "invoice_show",
  "invoices-received":  "invoice_show",
  "/invoices-received": "invoice_show",
  "price_lists":        "price_list_show",
  "/price_lists":       "price_list_show",
  "price-lists":        "price_list_show",
  "edpr_documents":     "edpr_show",
  "/edpr_documents":    "edpr_show",
  "edpr-parent":        "edpr_show",
  "/edpr-parent":       "edpr_show",
  "edpr_list":          "edpr_show",
  "/edpr_list":         "edpr_show",
  "edpr_requests":      "edpr_show",
  "/edpr_requests":     "edpr_show",
  "edpr_queue":         "edpr_show",
  "/edpr_queue":        "edpr_show",
  "automatyzacja":      "automation_show",
  "/automatyzacja":     "automation_show",
  "async_jobs":         "automation_show",
  "/async_jobs":        "automation_show",
  "admin-dashboard":    "automation_show",
  "/admin-dashboard":   "automation_show",
  "admin-tasks":        "automation_show",
  "/admin-tasks":       "automation_show",
  "logistics-dashboard":  "logistics_dashboard_show",
  "/logistics-dashboard": "logistics_dashboard_show",
  "logistics-pipeline":   "logistics_dashboard_show",
  "/logistics-pipeline":  "logistics_dashboard_show",
  "logistics-tasks":      "logistics_dashboard_show",
  "/logistics-tasks":     "logistics_dashboard_show",
  "sales-tasks":          "sales_dashboard_show",
  "/sales-tasks":         "sales_dashboard_show",
  "bank-parent":          "bank_show",
  "/bank-parent":         "bank_show",
  "bank-inbox":           "bank_show",
  "/bank-inbox":          "bank_show",
  "bank-statements":      "bank_show",
  "/bank-statements":     "bank_show",
  "bank-pairing":         "bank_show",
  "/bank-pairing":        "bank_show",
  "bank-payments":        "bank_show",
  "/bank-payments":       "bank_show",
  "payable-invoices":     "bank_show",
  "/payable-invoices":    "bank_show",
  "commission":           "commission_self_report_show",
  "/commission":          "commission_self_report_show",
  "commission-rules":        "commission_edit",
  "/commission-rules":       "commission_edit",
  "commission-reports":      "commission_self_report_show",
  "/commission-reports":     "commission_self_report_show",
  "commission-all-reports":  "commission_show",
  "/commission-all-reports": "commission_show",
  "commission-payees":       "commission_edit",
  "/commission-payees":      "commission_edit",
  "commission-edpr-rates":   "commission_edit",
  "/commission-edpr-rates":  "commission_edit",
  "commission-config":       "commission_edit",
  "/commission-config":      "commission_edit",
  // Settings children — gated individually so role tightening on any of these
  // hides the sider link automatically (route-level RequireRole already enforces).
  "settings/configuration":  "settings_show",
  "/settings/configuration": "settings_show",
  "template_configs":        "template_show",
  "/template_configs":       "template_show",
  "settings/gmail-oauth":    "settings_system_show",
  "/settings/gmail-oauth":   "settings_system_show",
  "settings/audit-log":      "settings_system_show",
  "/settings/audit-log":     "settings_system_show",
  // Refine nested keys are `/settings/settings/<x>`; the last-segment lookup
  // below needs bare names.
  "configuration":           "settings_show",
  "/configuration":          "settings_show",
  "gmail-oauth":             "settings_system_show",
  "/gmail-oauth":            "settings_system_show",
  "audit-log":               "settings_system_show",
  "/audit-log":              "settings_system_show",
  "transaction_rules":       "settings_show",
  "/transaction_rules":      "settings_show",
  "invoice_approval_rules":  "settings_show",
  "/invoice_approval_rules": "settings_show",
  "invoice_warning_clauses":  "settings_show",
  "/invoice_warning_clauses": "settings_show",
  "employees":               "employee_show",
  "/employees":              "employee_show",
  "delivery_targets":        "delivery_target_show",
  "/delivery_targets":       "delivery_target_show",
  "reports":                 "reports_show",
  "/reports":                "reports_show",
};

export function filterSiderItem(key: string, hasRole: (...roles: EmployeeRole[]) => boolean): boolean {
  // Refine's useMenu builds nested keys from parent names joined with "/",
  // e.g. "/commission/commission-all-reports" for a child of `commission`.
  // Try the full key first, then fall back to the last segment so gate
  // entries written as bare resource names still match deep items.
  const last = key.split("/").filter(Boolean).pop() ?? key;
  const gate =
    SIDER_KEY_GATES[key] ??
    SIDER_KEY_GATES[last] ??
    SIDER_KEY_GATES[`/${last}`];
  // Ungated => visible to everyone.
  if (!gate) return true;
  return hasRole(...[...ACCESS_GATES[gate]]);
}
